(ns renolit-roofing-service.view.service.zone-view
  (:require [clojure.string :as string]
            [re-frame.core :as rf]
            ["react" :refer [useEffect]]
            [renolit-roofing-service.config :as config]
            [renolit-roofing-service.layout.icon :as layout.icon]
            [renolit-roofing-service.localization :refer [t]]
            [renolit-roofing-service.shared.util.map :refer [get-by-id]]
            [renolit-roofing-service.util.geometry :as util.geometry]
            [renolit-roofing-service.util.google-maps :as google-maps]
            ["@vis.gl/react-google-maps" :refer [APIProvider ControlPosition Map MapControl useMapsLibrary]]))

(rf/reg-sub
 ::map-center
 (fn [db _]
   (:map-center db)))

(rf/reg-event-fx
 ::set-map-center
 (fn [{:keys [db]} [_ center]]
   {:db (assoc db :map-center center)}))

(defn- zone-card*
  [{:keys [name subzones reviews]} node-zones zIndex]
  [:div.zone-card
   {:class (when (even? zIndex) ["zone-card--white-bg"])}
   [:div.zone-card__header
    [:span.zone-card__title name]
    (->> reviews
         (filter (fn [rev] (= (:type rev) :focused)))
         (map (fn [rev]
                {:key (:id rev)
                 :wp (get rev (:wp rev))}))
         (distinct)
         (map (fn [wp] [google-maps/wp-marker wp "ml-4"])))
    (->> reviews
         (filter (fn [rev] (= (:type rev) :general)))
         (map (fn [rev]
                {:key (:id rev)
                 :wp (get rev (:wp rev))}))
         (distinct)
         (map (fn [wp] [google-maps/wp-marker wp "ml-4 general-review-wp-icon"])))]
   (when (seq subzones)
     [:div.zone-card__children
      (doall
       (for [zid subzones]
         ^{:key (str "zone-" zid)}
         [zone-card* (get-by-id node-zones zid) node-zones (inc zIndex)]))])])

(defn- zone-panel
  [root-zones node-zones]
  (fn []
    [:div.zone-panel
     (doall
      (for [zone root-zones]
        ^{:key (:id zone)}
        [zone-card* zone node-zones 0]))]))

(defn- legends
  []
  [:div.zone-legends
   [:div.zone-legends-icon
    [google-maps/wp-marker {:wp :legend} "mr-4"]
    (t [(keyword "domain.choices.revision-type/focused-revision")])]
   [:div.zone-legends-icon
    [google-maps/wp-marker {:wp :legend} "mr-4 general-review-wp-icon"]
    (t [(keyword "domain.choices.revision-type/general-revision")])]])

(defn- map-controls
  [root-zones node-zones]
  (fn []
    [:<>
     [:> MapControl {:position ControlPosition.TOP_LEFT}
      [zone-panel root-zones node-zones]]
     [:> MapControl {:position ControlPosition.BOTTOM_CENTER}
      [legends]]]))

(defn- work-package-card
  [{:keys [id wp wp-type tasks]} parent-zIndex]
  (let [wp-title (t [(keyword (str "domain.choices.wp/" (name wp)))])]
    [:div.work-package-card
     {:key id
      :class (when (odd? parent-zIndex) ["zone-card--white-bg"])}
     [:div.work-package-card__header
      [:span.work-package-card__title
       [google-maps/wp-icon {:key id :wp wp}]
       wp-title]]
     [:div.work-package-card__details
      (when (not= wp-type :nil)
        [:span.work-package-card__subtitle
         (t [(keyword (str "domain.choices.wp-type/" (name wp-type)))])])
      (doall
       (map-indexed
        (fn [idx {:keys [task-type comment]}]
          ^{:key (str (name task-type) "-" idx)}
          [:<>
           [:span.work-package-card__subtitle
            (t [(keyword (str "domain.choices.task-type/" (name task-type)))])]
           [:div.work-package-card__subtitle
            [layout.icon/icon {:key :message :class "mr-4"}]
            [:span.task-card__comments-text comment]]])
        tasks))]]))

(defn- work-package-group
  [reviews parent-zIndex]
  (let [reviews-by-type (group-by :type reviews)]
    [:<>
     (doall
      (for [[wp-type wp-reviews] reviews-by-type]
        ^{:key (str "review-type-" (name wp-type))}
        [:div.work-package-group
         {:class (when (even? parent-zIndex) ["work-package-group--white-bg"])}
         [:span.work-package-group__title
          (t [(keyword (str "page.zone." (name wp-type) "-revision"))])]
         [:div.work-package-group__list
          (doall
           (for [wp-review wp-reviews]
             ^{:key (str "review-" (:id wp-review))}
             [work-package-card wp-review parent-zIndex]))]]))]))

(defn- zone-header
  [zone-name z-index]
  [:div.zone-card__header
   [:<>
    [:span.zone-card__title
     {:class (when (> z-index 0) ["zone-card__title--subzone"])}
     zone-name]]])

(defn- zone-card
  [{:keys [name subzones reviews]} node-zones zIndex]
  [:div.zone-card
   {:class (when (odd? zIndex) ["zone-card--white-bg"])}
   [zone-header name zIndex]
   (when (or (seq reviews) (seq subzones))
     [:div.zone-card__children
      [work-package-group reviews zIndex]
      (doall
       (for [zid subzones]
         ^{:key (str "zone-" zid)}
         [:f> zone-card (get-by-id node-zones zid) node-zones (inc zIndex)]))])])

(defn- zone-hierarchy
  [root-zones node-zones]
  [:div.zones
   [:h1.zones__title (t [:common/zones-indicated])]
   [:div.zones__list
    (doall
     (for [zone root-zones]
       ^{:key (:id zone)}
       [:f> zone-card zone node-zones 0]))]])

(defn geocode-center
  [address]
  (let [geocoding-lib (useMapsLibrary "geocoding")]
    (useEffect
     (fn []
       (when (and geocoding-lib address)
         (let [^google.maps.GeocodingLibrary.Geocoder geocoder (new (.-Geocoder geocoding-lib))]
           (-> (.geocode geocoder (clj->js {:address address}))
               (.then (fn [response]
                        (let [resp (js->clj response {:keywordize-keys true})
                              loc (get-in resp [:results 0 :geometry :location])]
                          (rf/dispatch
                           [::set-map-center
                            {:lat (.lat loc)
                             :lng (.lng loc)}])))))))
       js/undefined)
     #js [geocoding-lib, address])
    [:<>]))

(defn- group-reviews-by-zone
  [{:keys [zones reviews]}]
  (let [subzone-ids (->> zones
                         (map :subzones)
                         (flatten)
                         (set))]
    (->> zones
         (mapv
          (fn [zone]
            (assoc
             zone
             :root?
             (not (contains? subzone-ids (:id zone)))
             :reviews
             (filterv
              (fn [review]
                (= (:zone-id review) (:id zone)))
              reviews)))))))

(defn- zones
  [service project]
  (let [{:keys [building-country building-region building-state building-city building-zip building-street]} project
        project-address (string/join ", " [building-street building-city building-zip building-state building-region building-country])
        center (rf/subscribe [::map-center])
        all-zones (group-reviews-by-zone service)
        root-zones (filter :root? all-zones)
        node-zones (filter #(= (:root? %) false) all-zones)]
    [:div.project_sections-outer-container
     {:style {:display "grid" :grid-template-columns "2fr 1fr"}}
     [:f> geocode-center project-address]
     (if @center
       [:> Map
        {:mapId "DEMO_MAP_ID"
         :mapTypeId "satellite"
         :tilt 0
         :defaultZoom 20
         :minZoom 18
         :style {:height "75vh"}
         :defaultCenter @center
         :disableDefaultUI true
         :zoomControl true
         :keyboardShortcuts false
         :restriction (clj->js
                       {:latLngBounds
                        (util.geometry/restriction-from-center
                         @center
                         {:delta-lat 0.003})
                        :strictBounds true})}
        [:f> google-maps/gmaps-bridge!]
        [:f> google-maps/zone-labels]
        [map-controls root-zones node-zones]]
       [:div {:style {:height "75vh"}}])
     [zone-hierarchy root-zones node-zones]]))

(defn main
  [service project]
  (let [config (rf/subscribe [::config/config-data])]
    (fn []
      (let [{:keys [google-maps-api-key]} @config]
        [:> APIProvider
         {:apiKey google-maps-api-key}
         [zones service project]]))))
